import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import { RepeatingAssumption } from '@futrli-sdk/predict';
import { AnyObject, useCrudActivityContext } from '@futrli/shared-services';

import {
  RepeatingMethod,
  getInitialRepeatingState,
  initializePredictionDetailsState
} from '../../../components/repeating/repeating-method';
import { AllowedAssumptionTypes } from '../../../../detail/reports/actions/actions';
import { TRACKING_EVENTS } from '../../../../../core/constants';
import { getRepeatingMethodData } from './utils';
import { PaymentTreatment, PredictionType } from '../../../types';
import { PredictionEditor } from '../prediction-editor/prediction-editor';
import { PredictionHeader } from '../prediction-header/prediction-header';
import { RepeatingMethodState } from '../../../components/repeating/types';
import { trackEvent } from '../../../../../core/tracker';
import { useStandardOrganisationPredictionBuilderContext } from '../../../context/standard-organisation-prediction-builder-context';
import { usePredictManagementContext } from '../../../../../predict-management/context/predict-management-context';

interface Props {
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const RepeatingPrediction = ({ onClose, isPredictionTypeActive }: Props) => {
  const {
    accountIndex,
    handleSaveExisting,
    handleSaveNew,
    isCashFlowDisabled,
    isEditing,
    isQuickCopy,
    paymentDataSaveParams,
    paymentTreatment,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    urlPredictionId,
    vatPercentage,
    previewImpactsResponse
  } = useStandardOrganisationPredictionBuilderContext();

  const { addCreating, removeCreating } = useCrudActivityContext();
  const { currency } = usePredictManagementContext();

  const [data, setData] = useState<RepeatingMethodState>(getInitialRepeatingState());
  const [validData, setValidData] = useState<RepeatingMethodState>();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isDataValid, setIsDataValid] = useState(false);

  const isActive = predictionType === PredictionType.REPEATING;
  const isFirstRun = useRef(true);
  const lastImpactPreviewParams = useRef<Partial<AllowedAssumptionTypes>>();

  // *********
  // * Utils *
  // *********

  const refreshAccountsImpactPreview = useCallback(async () => {
    if (accountIndex && !isCashFlowDisabled && validData) {
      const newParams = getRepeatingMethodData({
        predictionName: 'impact-data-request', // Dont use real predictionName here as it causes unneccesary refreshes
        vatPercentage,
        repeatingMethodData: validData,
        accountIndex,
        paymentTreatment
      });
      if (!isEqual(newParams, lastImpactPreviewParams.current)) {
        lastImpactPreviewParams.current = newParams;
        setImpactPreviewParams(newParams);
      }
    }
  }, [accountIndex, isCashFlowDisabled, vatPercentage, validData, paymentTreatment, setImpactPreviewParams]);

  const restoreInitialContent = useCallback(() => {
    setData(getInitialRepeatingState());
    setIsDataValid(false);
    return true;
  }, []);

  const restoreLoadedContent = useCallback(() => {
    if (predictionData) {
      setData(initializePredictionDetailsState(predictionData as RepeatingAssumption));
      return true;
    }
    return false;
  }, [predictionData]);

  const save = useCallback(async () => {
    const params = {
      ...data,
      name: predictionName,
      vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
      account_id: accountIndex,
      excluded_from_report: false,
      invoice_date: null,
      payment_type_data: paymentDataSaveParams
    };

    let success = false;
    if (urlPredictionId && data && !isQuickCopy) {
      success = await handleSaveExisting(params);
    } else {
      success = !!(await handleSaveNew(params));
      if (success) {
        trackEvent(TRACKING_EVENTS.CREATED_ANY_MANUAL_PREDICTION);
        trackEvent(TRACKING_EVENTS.CREATE_PREDICTION_REPEATING_NEW);
      }
    }
    return success;
  }, [
    accountIndex,
    data,
    handleSaveExisting,
    handleSaveNew,
    isQuickCopy,
    paymentDataSaveParams,
    paymentTreatment,
    predictionName,
    urlPredictionId,
    vatPercentage
  ]);

  // ************
  // * Handlers *
  // ************

  const handleContentChange = useCallback(
    (isValid: boolean, data: AnyObject) => {
      setPredictionContentTouched(true);
      setIsFormSubmitted(false);
      const newRepeatingData = (data as unknown) as RepeatingMethodState;
      setIsDataValid(isValid);
      setData(newRepeatingData);
      if (isValid) {
        setValidData(newRepeatingData);
      }
    },
    [setPredictionContentTouched]
  );

  const handleSave = useCallback(async () => {
    addCreating('repeating-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      onClose?.();
    }
    removeCreating('repeating-prediction');
  }, [addCreating, onClose, reloadForecastData, removeCreating, save]);

  const handleSaveAndNew = useCallback(async () => {
    addCreating('repeating-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      reset();
      resetUrlData();
      restoreInitialContent();
    }
    removeCreating('repeating-prediction');
  }, [addCreating, reloadForecastData, removeCreating, reset, restoreInitialContent, save, resetUrlData]);

  // ***********
  // * Effects *
  // ***********

  // Set initial / restore saved content on first run
  useEffect(() => {
    if (isFirstRun.current) {
      const contentWasRestored =
        (isEditing || isQuickCopy) && isActive ? restoreLoadedContent() : restoreInitialContent();
      if (contentWasRestored) {
        isFirstRun.current = false;
      }
    }
  }, [isActive, isEditing, restoreInitialContent, restoreLoadedContent, isQuickCopy]);

  // Refresh the accounts impacted info
  useEffect(() => {
    if (isActive) {
      refreshAccountsImpactPreview();
    } else {
      lastImpactPreviewParams.current = undefined;
    }
  }, [isActive, refreshAccountsImpactPreview]);

  // Update isSaveDisabled flag
  useEffect(() => {
    if (isActive) {
      setIsSaveDisabled(!isDataValid);
    }
  }, [isActive, isDataValid, setIsSaveDisabled]);

  return (
    <PredictionEditor
      onSave={handleSave}
      onSaveAndNew={handleSaveAndNew}
      show={isActive}
      onClose={onClose}
      isPredictionTypeActive={isPredictionTypeActive}
    >
      <RepeatingMethod
        currency={currency!}
        PredictionHeader={PredictionHeader}
        onChange={handleContentChange}
        repeatingMethodData={data}
        isFormSubmitted={isFormSubmitted}
        previewImpacts={previewImpactsResponse}
      />
    </PredictionEditor>
  );
};
